// The value slots are sized by their hidden sizer, which holds the settled value — so a count-up
// frame can be wider than the box it renders in, because a count-down starts on a longer number
// than it ends on (1,000.00 -> 950.00). Widening the box for those frames would move the header
// twice per count, so the digits are scaled to the box they already have instead: the panel keeps
// one width from the first frame to the last, and the value grows back to full size as it counts.
//
// The live span carries no `overflow: hidden` of its own — a transform scales an element's clip
// along with its content, so clipping here would survive the scale. The slot above it clips, and
// the fit keeps every frame inside it.
export function fitLiveValueToSlot(el: HTMLElement, duration: number): () => void {
  const range = document.createRange();
  let cancelled = false;
  let startTimestamp: number | null = null;
  // The scale we applied last frame, so the measurement below can be read back at 1:1.
  let scale = 1;

  const reset = () => {
    el.style.transform = '';
  };

  const step = (timestamp: number) => {
    if (cancelled) {
      return;
    }
    if (startTimestamp === null) {
      startTimestamp = timestamp;
    }

    // `clientWidth` is layout, so the transform never reaches it; the range rect is painted
    // geometry, so it does — divide it back out to get the width this frame's digits really want.
    const box = el.clientWidth;
    range.selectNodeContents(el);
    const text = range.getBoundingClientRect().width / scale;

    // `box` is 0 while the slot is hidden (the cash-reload swap), and `text` is 0 in jsdom, which
    // reports no geometry — both mean "nothing to fit", not "shrink to nothing".
    scale = box > 0 && text > box ? box / text : 1;
    el.style.transform = scale < 1 ? `scale(${scale})` : '';

    if (timestamp - startTimestamp < duration) {
      window.requestAnimationFrame(step);
    } else {
      reset();
    }
  };

  window.requestAnimationFrame(step);

  return () => {
    cancelled = true;
    reset();
  };
}
